/**
 * postizPending — the „wird bestätigt“ window, said out loud.
 *
 * Postiz answers QUEUE for every healthy live Facebook/Instagram publish. The
 * publish spine records that as `status:'accepted', postizOutcome:'accepted'`
 * and, until the server's reconcile cron (it runs every three minutes) turns it
 * into a real confirmation, refuses to edit, publish, regenerate, cancel or
 * delete that post. That refusal is correct — it is what makes a double post
 * impossible while nobody knows yet whether the first one went out.
 *
 * The way the refusal reached the operator was not. The caption silently went
 * read-only, the publish button silently disappeared, and the buttons that were
 * left answered 409. Nothing said the Beitrag was fine, that it clears itself,
 * or how long that takes — which is exactly the reported „ich habe gelöscht,
 * bekam einen Fehler, und konnte dann nicht mehr posten“.
 *
 * This module is the missing sentence, plus the rule that there is always
 * something the operator can still DO. Pure and time-injectable so the copy is
 * testable without mounting anything.
 *
 * NO ES2020 logical-assignment (`||=`/`&&=`/`??=`) — CI-guarded by
 * sourceLogicalAssignment.test.js.
 *
 * @module AdminSocialReviewerPage/postizPending
 */

const MINUTE_MS = 60 * 1000;

/**
 * The server's reconcile cron interval — the honest answer to "how long?".
 * Anything the operator is told about the wait is derived from this number, so
 * the UI cannot drift away from the schedule that actually clears the state.
 */
export const POSTIZ_RECONCILE_INTERVAL_MS = 3 * MINUTE_MS;

/**
 * Two full reconcile passes without a confirmation. Below this the wait is
 * ordinary and the copy stays calm; above it something is genuinely off and the
 * operator gets a different sentence with a way to check Postiz directly.
 */
export const POSTIZ_CONFIRMATION_OVERDUE_MS = 2 * POSTIZ_RECONCILE_INTERVAL_MS;

/**
 * A channel Postiz has taken but the platform has not confirmed yet — the
 * NORMAL result of every live publish, not an error.
 * @param {?Object} channel
 * @returns {boolean}
 */
export const isAwaitingPostizConfirmation = channel =>
    Boolean(channel) && channel.status === 'accepted' && channel.postizOutcome === 'accepted';

/**
 * Does this post carry at least one channel in that window? While it does, the
 * server locks every destructive/editing action on the WHOLE post, so this is
 * also the predicate behind the "why is this greyed out?" answers below.
 * @param {?Object} post
 * @returns {boolean}
 */
export const postAwaitsPostizConfirmation = post =>
    ((post && post.channels) || []).some(isAwaitingPostizConfirmation);

const stamp = value => {
    if (!value) return null;
    const ms = new Date(value).getTime();
    return Number.isFinite(ms) ? ms : null;
};

/**
 * How long this channel has been waiting, in ms, or null when the row carries
 * no acceptance stamp. `postizAcceptedAt` is written in the same assignment
 * that sets `postizOutcome:'accepted'`, so its absence means a legacy row —
 * we then simply say nothing about the elapsed time rather than guess.
 * @param {?Object} channel
 * @param {number} [now] epoch ms, injectable for tests
 * @returns {number|null}
 */
export const postizWaitedMs = (channel, now = Date.now()) => {
    const accepted = stamp(channel && channel.postizAcceptedAt);
    if (accepted === null) return null;
    return Math.max(0, now - accepted);
};

/**
 * "seit 2 Minuten" — the order of magnitude, never a ticking second count. The
 * operator needs to know whether this is normal, not to watch a clock.
 * @param {number} elapsedMs
 * @returns {string}
 */
export const waitedLabel = elapsedMs => {
    if (!Number.isFinite(elapsedMs) || elapsedMs < MINUTE_MS) return 'seit wenigen Sekunden';
    const minutes = Math.floor(elapsedMs / MINUTE_MS);
    return minutes === 1 ? 'seit 1 Minute' : `seit ${minutes} Minuten`;
};

/**
 * The whole message for one waiting channel: a headline that reads as progress
 * (because it IS progress), the reason the controls are locked, and — once the
 * wait stops being ordinary — the sentence that hands the operator Postiz.
 *
 * @param {?Object} channel
 * @param {number} [now] epoch ms, injectable for tests
 * @returns {?{headline:string, body:string, waited:?string, overdue:boolean, hint:?string}}
 *   null when the channel is not in this state
 */
export const postizPendingMessage = (channel, now = Date.now()) => {
    if (!isAwaitingPostizConfirmation(channel)) return null;
    const elapsed = postizWaitedMs(channel, now);
    const overdue = elapsed !== null && elapsed > POSTIZ_CONFIRMATION_OVERDUE_MS;
    return {
        headline: 'Gesendet — wird gerade bestätigt',
        body:
            'Der Beitrag ist bei Postiz angekommen. Die Bestätigung der Plattform holt ' +
            'HorseDeal automatisch, normalerweise innerhalb von drei Minuten. Solange sie ' +
            'aussteht, sind Text, Bild, Löschen und Neu generieren für diesen Beitrag ' +
            'gesperrt — nur so kann er nicht doppelt rausgehen. Du musst nichts tun.',
        waited: elapsed === null ? null : `Wartet ${waitedLabel(elapsed)}.`,
        overdue,
        hint: overdue
            ? 'Das dauert länger als üblich. Lade den Beitrag neu — bleibt es dabei, ' +
              'schau direkt in Postiz nach, ob er dort veröffentlicht wurde.'
            : null,
    };
};

/**
 * Why an action on the whole post is unavailable right now, in one sentence, or
 * null when this is not the reason. Callers use the SAME answer for the disabled
 * attribute and its tooltip, so a greyed-out control can always say what it is
 * waiting for instead of 409-ing after the click.
 * @param {?Object} post
 * @returns {string|null}
 */
export const postizPendingBlockReason = post =>
    postAwaitsPostizConfirmation(post)
        ? 'Ein Kanal wird gerade von Postiz bestätigt (meist unter drei Minuten). ' +
          'Danach ist das wieder möglich.'
        : null;
